import CloudOffRoundedIcon from '@mui/icons-material/CloudOffRounded';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import { checkBackendHealth, systemUnavailableVar } from '@/graphql/systemStatus';
import { BLUE, GREY } from '@/theme/palette';

const RETRY_INTERVAL_SECONDS = 10;

// Rendered as an opaque cover over the still-mounted app, so it must sit
// above every app surface (modals use zIndex up to 999999)
const OVERLAY_Z_INDEX = 2_000_000;

interface SystemUnavailableProps {
  onRetry?: () => void;
}

const SystemUnavailable: React.FC<SystemUnavailableProps> = ({ onRetry }) => {
  const [secondsLeft, setSecondsLeft] = useState(RETRY_INTERVAL_SECONDS);
  const [checking, setChecking] = useState(false);
  const unmounted = useRef(false);

  useEffect(() => {
    unmounted.current = false;
    return () => {
      unmounted.current = true;
    };
  }, []);

  const attemptRetry = useCallback(async () => {
    setChecking(true);
    const healthy = await checkBackendHealth();
    if (unmounted.current) return;
    setChecking(false);

    if (healthy) {
      onRetry ? onRetry() : systemUnavailableVar(false);
      return;
    }
    setSecondsLeft(RETRY_INTERVAL_SECONDS);
  }, [onRetry]);

  useEffect(() => {
    if (checking) return undefined;
    if (secondsLeft <= 0) {
      attemptRetry();
      return undefined;
    }
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft, checking, attemptRetry]);

  const handleManualRetry = () => {
    setSecondsLeft(RETRY_INTERVAL_SECONDS);
    attemptRetry();
  };

  return (
    <Box
      role="alert"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: OVERLAY_Z_INDEX,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.paper',
      }}
    >
      <Stack spacing={3} alignItems="center" textAlign="center" sx={{ maxWidth: 420, px: 3 }}>
        <CloudOffRoundedIcon sx={{ fontSize: 56, color: BLUE }} />
        <Typography variant="h5" fontWeight={600}>
          System Unavailable
        </Typography>
        <Typography variant="body1" sx={{ color: GREY }}>
          We're unable to reach our servers right now. This is usually temporary; we'll keep retrying automatically. If
          the issue persists, contact your administrator or our support team.
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button variant="contained" onClick={handleManualRetry} disabled={checking} sx={{ backgroundColor: BLUE }}>
            {checking ? 'Retrying…' : 'Retry now'}
          </Button>
          <Typography variant="body2" sx={{ color: GREY, minWidth: 160 }}>
            {checking ? 'Checking connection…' : `Retrying automatically in ${secondsLeft}s`}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

export default SystemUnavailable;
